// ---------------------------------------------------------------------------
// Franchise Schema (F15 Foundation)
// ---------------------------------------------------------------------------
// CROSS-TENANT BY DESIGN: pushed_assets links franchisor organization to
// franchisee tenants. franchisorTenantId = the franchisor pushing config.
// franchiseeTenantId = the location receiving config.
// Query routes MUST verify organization membership before returning data.

import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  jsonb,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const PUSHED_ASSET_TYPES = ["workflow", "template", "pipeline", "custom_field", "page"] as const;
export type PushedAssetType = (typeof PUSHED_ASSET_TYPES)[number];

export const PUSHED_ASSET_STATUSES = ["pending", "accepted", "rejected", "applied"] as const;
export type PushedAssetStatus = (typeof PUSHED_ASSET_STATUSES)[number];

// ---------------------------------------------------------------------------
// 1. Pushed Assets — Franchisor pushes config to franchisee locations
// ---------------------------------------------------------------------------

export const pushedAssets = pgTable("pushed_assets", {
  id: uuid("id").primaryKey().defaultRandom(),
  pushBatchId: uuid("push_batch_id").notNull(),
  // CQ4: Descriptive cross-tenant column names
  franchisorTenantId: uuid("franchisor_tenant_id").notNull(),
  franchiseeTenantId: uuid("franchisee_tenant_id").notNull(),
  assetType: text("asset_type").notNull(), // PushedAssetType
  assetId: uuid("asset_id").notNull(), // ID of the specific workflow/template/etc.
  assetName: text("asset_name").notNull(),
  assetConfig: jsonb("asset_config"), // Serialized config for the asset
  pushMode: text("push_mode").notNull().default("optional"),
  createdBy: text("created_by"),
  status: text("status").notNull().default("pending"), // PushedAssetStatus
  pushedAt: timestamp("pushed_at", tz).notNull().defaultNow(),
  appliedAt: timestamp("applied_at", tz),
  rejectedAt: timestamp("rejected_at", tz),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_pushed_asset_batch").on(table.pushBatchId),
  index("idx_pushed_asset_franchisor").on(table.franchisorTenantId),
  index("idx_pushed_asset_franchisee").on(table.franchiseeTenantId),
  index("idx_pushed_asset_status").on(table.franchiseeTenantId, table.status),
]);

// ---------------------------------------------------------------------------
// 2. Franchise Benchmarks — Cross-location performance comparison
// ---------------------------------------------------------------------------

export const franchiseBenchmarks = pgTable("franchise_benchmarks", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(), // The location being benchmarked
  franchisorTenantId: uuid("franchisor_tenant_id").notNull(), // The network owner
  metricName: text("metric_name").notNull(), // "email_open_rate" | "deal_close_rate" | etc.
  period: text("period").notNull(), // "2026-03" (monthly)
  benchmarkValue: numeric("benchmark_value", { precision: 10, scale: 4 }), // Network average
  currentValue: numeric("current_value", { precision: 10, scale: 4 }), // This location's value
  percentile: integer("percentile"), // 0-100: where this location ranks
  evaluatedAt: timestamp("evaluated_at", tz).notNull().defaultNow(),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_franchise_bench_tenant").on(table.tenantId),
  index("idx_franchise_bench_franchisor").on(table.franchisorTenantId),
  index("idx_franchise_bench_period").on(table.tenantId, table.period),
]);
